import React, { MutableRefObject, useEffect, useRef } from 'react';
import { Map, List } from 'immutable';
import { CUSTOM_PREFIX, SPORTS_PREFIX as SPORTS } from 'src/components/CustomersBar/CustomerTab/sports-config';
import { stakeFactorColor } from 'src/components/CustomersBar/utils';
import { Amount } from 'src_common/common/amount/Amount';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import Loading from 'src/components/Loading';
import {
    CustomerNotes,
    CustomerSummary,
    CustomerSummaryBalance,
    CustomerSummaryContent,
    CustomerSummaryContentWrapper,
    WarningIconWrapper,
    WarningInfo,
    WarningInfoText,
} from 'src/components/CustomersBar/CustomerTab/CustomerTab.styles';
import Tabs from 'src/components/Tabs';
import Icon from 'src/components/Icon/Icon';
import { MenuLinks } from 'src/components/CustomersBar/CustomerTab/Menu/MenuLinks';
import BetSlip from 'src/components/BetSlip';
import TraderChat from 'src/components/TraderChat';
import { CustomerTabState } from 'src/components/CustomersBar/CustomerTab/CustomerTab.state';
import { useCommon } from 'src/AppState/Common';

const SPORTS_PREFIX = Object.assign({}, SPORTS, CUSTOM_PREFIX);

export interface CustomerTabPropsType {
    bets: List<unknown>;
    data: Map<string, List<unknown>>;
    freeBetCredits: string;
    history: Map<string, unknown> | undefined;
    href: string;
    id: number;
    label: string;
    onSaveHistory: (id: unknown, data: unknown) => unknown;
    parameters: Map<string, unknown>;
    permissionGrants: List<unknown>;
    sports: Map<string, unknown>;
    status: string;
}
export const CustomerTab = observer('CustomerTab', (props: CustomerTabPropsType) => {
    const { data, id, status, permissionGrants, sports, freeBetCredits, history } = props;
    const appState = useAppStateContext();
    const common = useCommon();
    const prevProps: MutableRefObject<List<unknown>> = useRef(List());

    const state = CustomerTabState.get(common, id);

    // Temporary solution to save history, remove after refactoring CustomerBar.jsx
    useEffect(() => {
        if (prevProps.current.size < props.bets.size) {
            state.showBetslip('betslip');
        }

        prevProps.current = props.bets;

        return (): void => {
            const { onSaveHistory, id, parameters } = props;
            onSaveHistory(id, parameters);
        };
    });

    // Handle router alias updates
    useEffect(() => {
        const filterTab = appState.customerTabState.filterTab;
        const parametersRouter = appState.routerBackoffice.getParametersAllFromNamespace('customers');
        const customerHistory = history?.get(id.toString());

        let params: Record<string, unknown>;
        if (customerHistory !== undefined) {
            // @ts-expect-error
            params = customerHistory;
        } else if (Object.keys(parametersRouter).length > 0) {
            params = parametersRouter;
        } else {
            params = { tab: 'customer' };
        }
        if (filterTab !== undefined) {
            params = { ...params, auditFilter: filterTab };
        }
        // @ts-expect-error
        appState.router.onAlias(`customers:${id}`, params, true);
    }, [id, history, appState.customerTabState.filterTab, appState.routerBackoffice, appState.router]);

    const customerData = appState.models.getUserModel(id).basicData.data;
    const walletData = appState.models.getUserModel(id).walletData.data;
    const parameters = appState.routerBackoffice.getParametersAllFromNamespace('customers');
    const { failedWithdrawalsFundsEnabled } = appState.configState.config;
    if (customerData === null) {
        return null;
    }
    if (walletData === null) {
        return null;
    }

    const playableBalance = new Amount(walletData.playableBalance).format(walletData.currency);
    const freeBetCreditsValue = new Amount(freeBetCredits).format(walletData.currency);
    const withdrawable = new Amount(walletData.withdrawableBalance);
    const withdrawableFormated = new Amount(walletData.withdrawableBalance).format(walletData.currency);
    const creditLimit = new Amount(walletData.creditLimit).format(walletData.currency);
    const backofficeWithdrawable = new Amount(walletData.backofficeWithdrawableBalance);
    const failedWithdrawalsFunds = backofficeWithdrawable.sub(withdrawable).format(walletData.currency);

    const renderSportsStakes = (stakesSports: List<unknown> | undefined): JSX.Element | null => {
        if (stakesSports === undefined) {
            return null;
        }
        /*
            Ex. format:
            [
                {"sport": "football", "sf": 0.3},
                {"sport": "golf", "sf": 1.3}
                ...
            ]
        */

        // Get sports stakes, sort them by stake from bigger to smaller
        // and get only the first top sports with biggest stake to show

        const stakesItemsDOM = stakesSports
            .sort((currentSport, nextSport) => {
                // Get sport stake
                // @ts-expect-error
                const currStake = currentSport.get('sf');
                // @ts-expect-error
                const nextStake = nextSport.get('sf');

                if (currStake < nextStake) {
                    return 1;
                }
                if (currStake > nextStake) {
                    return -1;
                }

                return 0;
            })
            .slice(0, 3)
            .map((sportObj) => {
                // @ts-expect-error
                const sportName = sportObj.get('sport');
                // @ts-expect-error
                const stake = parseFloat(sportObj.get('sf'));

                return (
                    <span
                        className='customer-summary-stake-factor-sport'
                        key={sportName}
                        //@ts-expect-error
                        title={`${SPORTS_PREFIX[sportName].name} ${stake}`}
                    >
                        <span className='customer-summary-stake-factor-sport-name'>
                            {
                                //@ts-expect-error
                                SPORTS_PREFIX[sportName].code
                            }
                        </span>
                        <span
                            className='customer-summary-stake-factor-sport-value'
                            style={{ color: stakeFactorColor(stake) }}
                        >
                            {stake}
                        </span>
                    </span>
                );
            })
            .toArray();

        return stakesItemsDOM.length > 0 ? (
            <div className='customer-summary-stake-factor-sports'> {stakesItemsDOM} </div>
        ) : null;
    };

    if (status === 'IN_PROGRESS' || status === 'ERROR') {
        return (
            <div className='customer-tab'>
                <Loading
                    message='Loading customer...'
                    active={props.status !== 'FINISHED'}
                />
                <CustomerSummary>
                    <CustomerSummaryContent isActiveAccount={state.isActiveStatus}>
                        <Tabs
                            className='customer-summary-notes tabs__horizontal'
                            style={{ visibility: 'hidden' }}
                        >
                            <CustomerNotes
                                key='trading-notes'
                                label='Trading notes'
                            >
                                ...
                            </CustomerNotes>
                            <CustomerNotes
                                key='security-notes'
                                label='Security notes'
                            >
                                ...
                            </CustomerNotes>
                        </Tabs>

                        <CustomerSummaryBalance failedWithdrawalsFundsEnabled={failedWithdrawalsFundsEnabled}>
                            <li>
                                Playable balance: <strong>{playableBalance}</strong>
                            </li>
                            <li>
                                Free Bet Credits: <Icon name='gift' />
                                <strong>{freeBetCreditsValue}</strong>
                            </li>
                            <li>
                                Withdrawable balance: <strong>{withdrawableFormated}</strong>
                            </li>
                            {failedWithdrawalsFundsEnabled ? (
                                <li>
                                    Failed withdrawals funds: <strong>{failedWithdrawalsFunds}</strong>
                                </li>
                            ) : null}
                            <li>
                                Credit limit: <strong>{creditLimit}</strong>
                            </li>
                        </CustomerSummaryBalance>

                        <div
                            className='customer-summary-stake-factor'
                            style={{ visibility: 'hidden' }}
                        >
                            <span>Stake factor:</span>
                            <strong style={{ background: stakeFactorColor(0.5) }}>n/a</strong>
                        </div>
                    </CustomerSummaryContent>
                </CustomerSummary>

                {status === 'ERROR' ? (
                    <div className='error-message'>
                        <div className='error-message-content'>Error occurred.</div>
                    </div>
                ) : undefined}
            </div>
        );
    }

    return (
        <div className='customer-tab'>
            <Loading
                message='Loading customer...'
                active={status === 'IN_PROGRESS'}
            />

            <CustomerSummary>
                <CustomerSummaryContent isActiveAccount={state.isActiveStatus}>
                    {state.isActiveStatus === false ? (
                        <WarningInfo>
                            <WarningIconWrapper />
                            <WarningInfoText>
                                Account
                                <br />
                                not active
                            </WarningInfoText>
                        </WarningInfo>
                    ) : null}

                    <Tabs className='customer-summary-notes tabs__horizontal'>
                        <CustomerNotes
                            key='trading-notes'
                            label='Trading notes'
                        >
                            {customerData.tradingNotes}
                        </CustomerNotes>
                        <CustomerNotes
                            key='security-notes'
                            label='Security notes'
                        >
                            {customerData.securityNotes}
                        </CustomerNotes>
                    </Tabs>

                    <CustomerSummaryBalance
                        failedWithdrawalsFundsEnabled={failedWithdrawalsFundsEnabled}
                        data-test='customer-summary-balance'
                    >
                        <li data-test='customer-summary-playable-balance'>
                            Playable balance: <strong>{playableBalance}</strong>
                        </li>
                        <li data-test='customer-summary-free-bet-credits'>
                            Free Bet Credits: <Icon name='gift' />
                            <strong> {freeBetCreditsValue}</strong>
                        </li>
                        <li data-test='customer-summary-withdrawable-balance'>
                            Withdrawable balance: <strong>{withdrawableFormated}</strong>
                        </li>
                        {failedWithdrawalsFundsEnabled ? (
                            <li data-test='customer-summary-failed-withdrawals-funds'>
                                Failed withdrawals funds: <strong>{failedWithdrawalsFunds}</strong>
                            </li>
                        ) : null}
                        <li data-test='customer-summary-credit-limit'>
                            Credit limit: <strong>{creditLimit}</strong>
                        </li>
                    </CustomerSummaryBalance>

                    <div className='customer-summary-stake-factor-wrap'>
                        <div className='customer-summary-stake-factor'>
                            <span>Prematch SF</span>
                            <strong style={{ background: stakeFactorColor(customerData.stakeFactor) }}>
                                {customerData.stakeFactor}
                            </strong>
                        </div>

                        {renderSportsStakes(data.get('stakeFactorSports'))}
                    </div>

                    <div className='customer-summary-stake-factor-wrap'>
                        <div className='customer-summary-stake-factor'>
                            <span>Show/In Play SF</span>
                            <strong style={{ background: stakeFactorColor(customerData.stakeFactorInPlay) }}>
                                {customerData.stakeFactorInPlay}
                            </strong>
                        </div>
                        {renderSportsStakes(data.get('stakeFactorInPlaySports'))}
                    </div>
                </CustomerSummaryContent>
            </CustomerSummary>

            <CustomerSummaryContentWrapper>
                <MenuLinks
                    id={id}
                    onQueryAudits={state.onQueryAudits}
                    customerData={customerData}
                    permissionGrants={permissionGrants}
                    onShowAll={state.onShowAll}
                />

                <>
                    <Tabs
                        allowNoActive={true}
                        active={parameters['betslip'] === 'betslip' ? 'betslip' : null}
                        className='tabs__customer-main tabs__customer-main--betslip'
                        onChange={state.toggleBetslip}
                    >
                        <div
                            //@ts-expect-error
                            label='Betslip'
                            key='betslip'
                            data-test='betslip-tab'
                        >
                            <BetSlip
                                id={id}
                                sports={sports}
                            />
                        </div>
                    </Tabs>

                    <Tabs
                        allowNoActive={true}
                        active={parameters['chat'] === undefined ? null : 'chat'}
                        className='tabs__customer-main tabs__customer-main--betslip'
                        onChange={state.toggleTraderChat}
                    >
                        <div
                            //@ts-expect-error
                            label='Trader chat'
                            key='chat'
                            className='tabs__customer-main-trader-chat'
                            data-test='trader-chat-tab'
                        >
                            <TraderChat
                                chatId={props.id}
                                localStorage={appState.localStorageState}
                            />
                        </div>
                    </Tabs>
                </>
            </CustomerSummaryContentWrapper>
        </div>
    );
});
